import { getBackendUrl } from './config';

type UnauthorizedHandler = () => void;
let onUnauthorized: UnauthorizedHandler | null = null;

export const setOnUnauthorized = (handler: UnauthorizedHandler | null) => {
  onUnauthorized = handler;
};

export const apiFetch = async <T = unknown>(
  token: string | null,
  path: string,
  init: RequestInit = {},
  responseType: 'auto' | 'blob' | 'raw' = 'auto'
): Promise<T> => {
  const url = `${getBackendUrl()}${path.startsWith('/') ? path : '/' + path}`;
  const headers = new Headers(init.headers || {});
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const res = await fetch(url, { ...init, headers });
  if (res.status === 401) {
    onUnauthorized?.();
    throw new Error('Unauthorized');
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Request failed: ${res.status}`);
  }
  if (res.status === 204) return undefined as T;
  // The raw Response — the caller reads the body AND the headers itself
  // (e.g. a CSV download needs the row-count headers).
  if (responseType === 'raw') {
    return res as unknown as T;
  }
  // File downloads keep their raw bytes — decoding them as text would drop the
  // UTF-8 BOM that makes Excel read the file as UTF-8.
  if (responseType === 'blob') {
    return (await res.blob()) as unknown as T;
  }
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) {
    return (await res.json()) as T;
  }
  return (await res.text()) as unknown as T;
};
